import { useTranslation } from 'react-i18next';
import type { Control, FieldValues } from 'react-hook-form';
import {
  Flex,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Markdown,
} from '@kognitos/lattice';
import {
  Bdkv2CredentialType,
  type Bdkv2BookDescriptor,
  type Bdkv2CredentialDescriptor,
} from '@/generated/hooks/bdk/models';
import { getNoAutofillProps } from '@/shared/utils';
import { isKeyValueCredential } from '../utils';
import { CredentialKeyValueInput } from './CredentialKeyValueInput';
import { CredentialSensitiveValueInput } from './CredentialSensitiveValueInput';

export interface ICustomCredentialsStepProps {
  integration: Bdkv2BookDescriptor;
  authMethodId: string;
  control: Control<FieldValues>;
  fieldPrefix?: string;
  disabled?: boolean;
  readOnly?: boolean;
  /** When true, shows a hint that saved credentials are not displayed. */
  isEditMode?: boolean;
}

export function CustomCredentialsStep({
  integration,
  authMethodId,
  control,
  fieldPrefix = '',
  disabled = false,
  readOnly = false,
  isEditMode = false,
}: ICustomCredentialsStepProps) {
  // Find the selected authentication method
  const selectedAuth = integration.authentications?.find(auth => {
    const authId = auth.custom?.id;
    return authId === authMethodId;
  });

  // Get credentials from the selected auth method
  const credentials: Bdkv2CredentialDescriptor[] =
    selectedAuth?.custom?.credentials || [];

  const { t: tIntegrations } = useTranslation('integrations');

  return (
    <div className="space-y-4 w-full">
      {credentials.map(credential => {
        const fieldName = credential.id || '';
        const fullFieldName = fieldPrefix
          ? `${fieldPrefix}.${fieldName}`
          : fieldName;

        return (
          <FormField
            key={fullFieldName}
            control={control}
            name={fullFieldName}
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <Flex align="center" gap={2}>
                    {credential.label || credential.id || 'Credential'}
                    {disabled && (
                      <span className="text-xs text-muted-foreground leading-[inherit]">
                        {tIntegrations(
                          'integrations.connections.copiedFromDraft',
                        )}
                      </span>
                    )}
                  </Flex>
                </FormLabel>
                <FormControl>
                  {isKeyValueCredential(credential) ? (
                    <CredentialKeyValueInput
                      field={field}
                      disabled={disabled}
                      readOnly={!disabled && readOnly}
                      showEditHint={isEditMode}
                    />
                  ) : (
                    <Flex align="center" gap={2}>
                      {credential.type ===
                      Bdkv2CredentialType.CredentialTypeSensitiveText ? (
                        <CredentialSensitiveValueInput
                          data-testid={`connection-input-${credential.id}`}
                          field={field}
                          placeholder={tIntegrations(
                            'integrations.customCredentials.placeholder',
                            {
                              fieldLabel: credential.label || 'value',
                            },
                          )}
                          disabled={disabled}
                          readOnly={!disabled && readOnly}
                          className="flex-1"
                        />
                      ) : (
                        <Input
                          data-testid={`connection-input-${credential.id}`}
                          type="text"
                          placeholder={tIntegrations(
                            'integrations.customCredentials.placeholder',
                            {
                              fieldLabel: credential.label || 'value',
                            },
                          )}
                          disabled={disabled}
                          readOnly={!disabled && readOnly}
                          {...field}
                          {...getNoAutofillProps()}
                          value={field.value || ''}
                          className="full-width"
                        />
                      )}
                    </Flex>
                  )}
                </FormControl>
                {credential.description && (
                  <FormDescription>
                    <Markdown
                      textProps={{
                        level: 'small',
                        color: 'muted',
                        weight: 'normal',
                      }}
                    >
                      {credential.description}
                    </Markdown>
                  </FormDescription>
                )}
                <FormMessage />
              </FormItem>
            )}
          />
        );
      })}

      {credentials.length === 0 && !isEditMode && (
        <div className="text-sm text-muted-foreground">
          {tIntegrations('integrations.customCredentials.noCredentials')}
        </div>
      )}
    </div>
  );
}
